/** admin/sessions — API (worlds + world-sessions + selector lists). */
import { http } from '../../_shared/http';
import type {
  WorldItem,
  SessionItem,
  UpsertSessionPayload,
  CreateWorldPayload,
  UpdateWorldPayload,
  GameItem,
  LocalSaveItem,
  SessionScriptItem,
  WorldSaveResetResult,
} from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapWorld = (w: any): WorldItem => ({
  id: w.id,
  name: w.name,
  gameName: w.game?.name ?? w.gameName ?? '',
  isActive: w.isActive,
  configJson: w.configJson,
  createdAt: w.createdAt,
  updatedAt: w.updatedAt,
});

export const sessionsApi = {
  // Selector lists
  async listGames(): Promise<GameItem[]> {
    const data = await http.get<{ data?: { id: string; name: string }[] }>('/games/list-all?limit=100');
    return (data.data ?? []).map((g) => ({ id: g.id, name: g.name }));
  },

  /**
   * The game server's local saves. A list it could not read fails the query,
   * which the world form says, rather than reading as no saves.
   */
  async listLocalSaves(): Promise<LocalSaveItem[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = await http.get<{ saves: any[] }>('/worlds/local-saves');
    return data.saves.map((s) => ({
      saveId: s.save_id,
      worldName: s.world_name,
      createdAt: s.created_at,
      gameTicks: s.game_ticks,
      teamCount: s.team_count,
    }));
  },

  /**
   * The session scripts the game server has. A list it could not read fails
   * the query, which the session form and Launch Match say, rather than
   * reading as no scripts.
   */
  async listSessionScripts(): Promise<SessionScriptItem[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = await http.get<{ session_scripts: any[] }>('/worlds/session-scripts');
    return data.session_scripts.map((e) => ({ id: e.id, steps: e.steps ?? [] }));
  },

  // Worlds
  async listWorlds(page = 1, limit = 100): Promise<WorldItem[]> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    const data = await http.get<{ data?: unknown[] }>(`/worlds/list-all?${params.toString()}`);
    return (data.data ?? []).map(mapWorld);
  },

  async createWorld(payload: CreateWorldPayload): Promise<WorldItem> {
    return mapWorld(
      await http.post<unknown>('/worlds/create-by-game-name', {
        gameName: payload.gameName,
        worldName: payload.worldName,
        configJson: payload.configJson,
      }),
    );
  },

  async updateWorld(id: string, payload: UpdateWorldPayload): Promise<WorldItem> {
    return mapWorld(await http.patch<unknown>(`/worlds/update/${id}`, payload));
  },

  deleteWorld: (id: string) => http.del<void>(`/worlds/delete/${id}`),

  /** Erases the world's saved progress for every team, ending its running matches first. */
  resetWorldSave: (id: string) => http.post<WorldSaveResetResult>(`/worlds/reset-save/${id}`),

  // Sessions
  listSessions: (gameName: string, worldName: string) => {
    const params = new URLSearchParams({ gameName, worldName });
    return http.get<SessionItem[]>(`/world-sessions/by-world?${params.toString()}`);
  },

  createSession: (payload: UpsertSessionPayload) => http.post<SessionItem>('/world-sessions/create', payload),

  updateSession: (payload: UpsertSessionPayload) => http.patch<SessionItem>('/world-sessions/update', payload),

  deleteSession: (gameName: string, worldName: string, externalId: string) => {
    const params = new URLSearchParams({ gameName, worldName, externalId });
    return http.del<void>(`/world-sessions/delete?${params.toString()}`);
  },
};
